import React, { useState, useEffect, useCallback } from "react";
import redirectURL from "../redirectURL.js";
import SweetAlert from "react-bootstrap-sweetalert";
import { getUserIdentityFields } from "../common/utils";
import {
    DEFAULT_PERFORMANCE_CONFIG,
    performanceLegendItems,
} from "./msilDashboard/performanceConfig";

// Hub Performance Config - the Excellent / Good / Poor bands the Hub Performance Overview
// on the Live and Detailed dashboards colours its hubs by.
//
// One global setting, deliberately not scoped to a warehouse / hub / port: the bands are a
// company-wide service standard. That is also why this screen does not call resolvePageAccess
// like the port-scoped masters do - that helper sets the navbar's selected loading port as a
// side effect, which would silently move the user's port just for opening this page. Editing
// is gated on the same admin check resolvePageAccess itself uses for full (3) access.
const HubPerformanceConfig = () => {

    const [excellentMin, setExcellentMin] = useState(String(DEFAULT_PERFORMANCE_CONFIG.excellent_min));
    const [goodMin, setGoodMin] = useState(String(DEFAULT_PERFORMANCE_CONFIG.good_min));
    const [saved, setSaved] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: "info", title: "" });

    var role = localStorage.getItem("roles") || "";
    var isDeptAdmin = localStorage.getItem("is_department_admin");
    var canEdit = role.indexOf("lm_site_admin") >= 0 || role.indexOf("lm_admin") >= 0 || isDeptAdmin == 1;

    const showAlert = function (title, type) {
        setAlert({ show: true, type: type, title: title });
    };
    const closeAlert = function () {
        setAlert({ show: false, type: "info", title: "" });
    };

    const loadConfig = useCallback(function () {
        setLoading(true);
        redirectURL.post("/msildashboard/getHubPerformanceConfig", {})
            .then(function (response) {
                var body = response && response.data ? response.data : {};
                if (body.status === "Success" && body.config) {
                    setExcellentMin(String(body.config.excellent_min));
                    setGoodMin(String(body.config.good_min));
                    setSaved({
                        excellent_min: body.config.excellent_min,
                        good_min: body.config.good_min,
                        updated_at: body.updated_at || null,
                        updated_by: body.updated_by || null,
                    });
                } else {
                    showAlert(body.message || "Could not load the performance parameters", "error");
                }
                setLoading(false);
            })
            .catch(function () {
                showAlert("Could not load the performance parameters", "error");
                setLoading(false);
            });
    }, []);

    useEffect(function () { loadConfig(); }, [loadConfig]);

    var excellent = Number(excellentMin);
    var good = Number(goodMin);

    // Validated on every keystroke so Save can stay disabled rather than fail on submit
    var validationError = "";
    if (excellentMin === "" || goodMin === "" || !isFinite(excellent) || !isFinite(good)) {
        validationError = "Enter both bands as numbers";
    } else if (excellent < 0 || excellent > 100 || good < 0 || good > 100) {
        validationError = "Bands must be between 0 and 100";
    } else if (good > excellent) {
        validationError = "Good cannot start above Excellent - nothing would ever be Good";
    }

    var isDirty = saved !== null && (excellent !== saved.excellent_min || good !== saved.good_min);

    const onSave = function () {
        if (validationError) {
            showAlert(validationError, "warning");
            return;
        }
        var identity = getUserIdentityFields();
        setSaving(true);
        redirectURL.post("/msildashboard/updateHubPerformanceConfig", {
            excellent_min: excellent,
            good_min: good,
            updated_by: identity.email || identity.user_name || "system",
        })
            .then(function (response) {
                var body = response && response.data ? response.data : {};
                setSaving(false);
                if (body.status === "Success") {
                    showAlert("Performance parameters updated", "success");
                    loadConfig();
                } else {
                    showAlert(body.message || "Could not update the performance parameters", "error");
                }
            })
            .catch(function () {
                setSaving(false);
                showAlert("Could not update the performance parameters", "error");
            });
    };

    const onReset = function () {
        if (!saved) return;
        setExcellentMin(String(saved.excellent_min));
        setGoodMin(String(saved.good_min));
    };

    // Band strip: the whole 0-100 range split the way the dashboards read it
    var poorWidth = validationError ? 0 : good;
    var goodWidth = validationError ? 0 : Math.max(excellent - good, 0);
    var excellentWidth = validationError ? 0 : Math.max(100 - excellent, 0);

    var bands = [
        { key: "poor", label: "Poor", color: "#d32f2f", width: poorWidth, range: "< " + good + "%" },
        { key: "good", label: "Good", color: "#f9a825", width: goodWidth, range: good + "% to <" + excellent + "%" },
        { key: "excellent", label: "Excellent", color: "#2e7d32", width: excellentWidth, range: "≥ " + excellent + "%" },
    ];

    // 0 and 100 anchor the ends; the two band boundaries are placed where they actually fall.
    // A boundary within 6% of an end would print on top of it, so the end is dropped instead.
    var boundaryTicks = [];
    if (!validationError) {
        if (good > 6) boundaryTicks.push({ key: "zero", at: 0, label: "0%", align: "start" });
        boundaryTicks.push({ key: "good", at: good, label: good + "%", boundary: true });
        // Both boundaries at the same value would print the same number twice
        if (excellent !== good) {
            boundaryTicks.push({ key: "excellent", at: excellent, label: excellent + "%", boundary: true });
        }
        if (excellent < 94) boundaryTicks.push({ key: "hundred", at: 100, label: "100%", align: "end" });
    }

    var legendItems = performanceLegendItems({ excellent_min: excellent, good_min: good });

    var fieldStyle = { maxWidth: "150px" };
    var cardStyle = {
        background: "#fff", borderRadius: "8px", padding: "22px 24px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "16px",
    };
    var sectionTitle = {
        fontSize: "13px", fontWeight: 700, color: "#1a237e",
        textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "14px",
    };

    return (
        <div className="hub-master-container d-flex flex-column" style={{ height: "100%", overflowY: "auto" }}>
            <SweetAlert
                show={alert.show}
                type={alert.type}
                title={alert.title}
                onConfirm={closeAlert}
                styles={{ zIndex: "10000001" }}
            >
            </SweetAlert>

            {/* HEADER */}
            <div className="master-header">
                <div className="d-flex align-items-center" style={{ gap: "14px" }}>
                    <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                    <div>
                        <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e", letterSpacing: "0.2px" }}>Hub Performance Config</h5>
                        <div style={{ fontSize: "11px", color: "#78909c", marginTop: "2px" }}>
                            Applies to the Hub Performance Overview on the Live and Detailed dashboards
                        </div>
                    </div>
                </div>

                {canEdit && <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <button
                        className="pill-btn pill-btn-info"
                        onClick={onReset}
                        disabled={loading || saving || !isDirty}
                    >
                        Reset
                    </button>
                    <button
                        className="pill-btn pill-btn-success"
                        onClick={onSave}
                        disabled={loading || saving || !!validationError || !isDirty}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>}
            </div>

            {loading ? (
                <div style={Object.assign({}, cardStyle, { marginTop: "12px" })}>Loading...</div>
            ) : (
                <div className="mt-2">
                    {/* BANDS */}
                    <div style={cardStyle}>
                        <div style={sectionTitle}>Performance Bands</div>

                        <div className="d-flex" style={{ gap: "28px", flexWrap: "wrap", alignItems: "flex-start" }}>
                            <div>
                                <label className="c-lbl" style={{ display: "block" }}>
                                    Excellent from (%) <span style={{ color: "#d32f2f" }}>*</span>
                                </label>
                                <input
                                    type="number" step="0.01" min="0" max="100"
                                    className="form-control" style={fieldStyle}
                                    value={excellentMin}
                                    disabled={!canEdit}
                                    onChange={function (e) { setExcellentMin(e.target.value); }}
                                />
                                <div style={{ fontSize: "11px", color: "#78909c", marginTop: "4px" }}>
                                    At or above this is Excellent
                                </div>
                            </div>

                            <div>
                                <label className="c-lbl" style={{ display: "block" }}>
                                    Good from (%) <span style={{ color: "#d32f2f" }}>*</span>
                                </label>
                                <input
                                    type="number" step="0.01" min="0" max="100"
                                    className="form-control" style={fieldStyle}
                                    value={goodMin}
                                    disabled={!canEdit}
                                    onChange={function (e) { setGoodMin(e.target.value); }}
                                />
                                <div style={{ fontSize: "11px", color: "#78909c", marginTop: "4px" }}>
                                    At or above this but below Excellent is Good
                                </div>
                            </div>
                        </div>

                        {validationError ? (
                            <div style={{ marginTop: "14px", color: "#d32f2f", fontSize: "12px", fontWeight: 600 }}>
                                {validationError}
                            </div>
                        ) : null}
                    </div>

                    {/* PREVIEW */}
                    <div style={cardStyle}>
                        <div style={sectionTitle}>Preview</div>

                        {validationError ? (
                            <div style={{ fontSize: "12px", color: "#78909c" }}>
                                Fix the bands above to see the preview.
                            </div>
                        ) : (
                            <div>
                                {/* 0-100 strip, proportional to the configured bands */}
                                <div className="d-flex" style={{ height: "26px", borderRadius: "4px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
                                    {bands.map(function (band) {
                                        if (band.width <= 0) return null;
                                        return (
                                            <div
                                                key={band.key}
                                                title={band.label + " " + band.range}
                                                style={{
                                                    width: band.width + "%", background: band.color, color: "#fff",
                                                    fontSize: "11px", fontWeight: 600, display: "flex",
                                                    alignItems: "center", justifyContent: "center", overflow: "hidden",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {band.width >= 12 ? band.label : ""}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Scale: each boundary sits at its own position on the bar, so the
                                    numbers line up with where the colour actually changes. */}
                                <div style={{ position: "relative", height: "20px", marginTop: "2px", fontSize: "11px", color: "#78909c" }}>
                                    {boundaryTicks.map(function (tick) {
                                        return (
                                            <span
                                                key={tick.key}
                                                style={{
                                                    position: "absolute", left: tick.at + "%", top: 0,
                                                    transform: tick.align === "start" ? "none"
                                                        : tick.align === "end" ? "translateX(-100%)" : "translateX(-50%)",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: tick.boundary ? 600 : 400,
                                                    color: tick.boundary ? "#455a64" : "#78909c",
                                                }}
                                            >
                                                {tick.boundary ? (
                                                    <span style={{
                                                        display: "block", width: "1px", height: "5px",
                                                        background: "#b0bec5", margin: "0 auto 2px auto",
                                                    }} />
                                                ) : null}
                                                {tick.label}
                                            </span>
                                        );
                                    })}
                                </div>

                                {/* Exactly what the dashboards will render */}
                                <div style={{ marginTop: "18px" }}>
                                    <div style={{ fontSize: "11px", color: "#78909c", marginBottom: "6px" }}>
                                        Dashboard legend
                                    </div>
                                    <div className="d-flex" style={{ gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
                                        {legendItems.map(function (item) {
                                            var dotColor = item.dot === "greenFill" ? "#2e7d32"
                                                : item.dot === "yellowFill" ? "#f9a825" : "#d32f2f";
                                            return (
                                                <span key={item.dot} className="d-flex align-items-center" style={{ gap: "6px", fontSize: "12px" }}>
                                                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: dotColor, display: "inline-block" }} />
                                                    {item.text}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* FOOTER */}
                    <div style={{ fontSize: "11px", color: "#78909c", paddingLeft: "4px", paddingBottom: "16px" }}>
                        {saved && saved.updated_at
                            ? "Last updated " + new Date(saved.updated_at).toLocaleString() +
                              (saved.updated_by ? " by " + saved.updated_by : "")
                            : "Not saved yet - the dashboards are using the default bands."}
                        {!canEdit ? "  |  View only - you do not have edit rights for this master." : ""}
                        {isDirty ? "  |  Unsaved changes" : ""}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HubPerformanceConfig;
